import ky, { type KyInstance, HTTPError } from 'ky';
import { env } from '@shared/config/env';
import { parseProblemDetail, ApiError } from './problem-detail';
import { tokenStorage, type AuthTokens } from './tokens';
import { tryMockMatch } from './mock/handlers';

const VENDOR_PREFIX = '/api/vendor/v1';

type RefreshHandler = () => Promise<AuthTokens | null>;
type UnauthorizedHandler = () => void;

let refreshHandler: RefreshHandler | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let inflightRefresh: Promise<AuthTokens | null> | null = null;

export function registerAuthHandlers(opts: {
  onRefresh: RefreshHandler;
  onUnauthorized: UnauthorizedHandler;
}): void {
  refreshHandler = opts.onRefresh;
  unauthorizedHandler = opts.onUnauthorized;
}

// Any path under /auth/** is part of the public auth zone — we must not send
// (a potentially stale) Bearer there, and we must not try the auto-refresh
// dance on 401s coming from those endpoints. /account/** still requires auth.
function isAuthEndpoint(url: string): boolean {
  return url.includes(`${VENDOR_PREFIX}/auth/`);
}

async function runRefresh(): Promise<AuthTokens | null> {
  if (!refreshHandler) return null;
  inflightRefresh ??= refreshHandler().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

export const http: KyInstance = ky.create({
  prefixUrl: `${env.apiBaseUrl}${VENDOR_PREFIX}`.replace(/\/+$/, ''),
  timeout: 30_000,
  retry: 0,
  hooks: {
    beforeRequest: [
      (request) => {
        if (isAuthEndpoint(request.url)) {
          // Never decorate the public auth zone with a Bearer — if a stale
          // access token leaked through (e.g. during refresh), strip it.
          request.headers.delete('Authorization');
        } else {
          const token = tokenStorage.getAccess();
          if (token && !request.headers.has('Authorization')) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        }
        if (!request.headers.has('Accept-Language')) {
          const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en';
          request.headers.set('Accept-Language', lang.startsWith('ru') ? 'ru' : 'en');
        }
      },
      // Mock layer: intercept everything and serve canned responses when enabled.
      ...(env.mock ? [(request: Request) => tryMockMatch(request)] : []),
    ],
    afterResponse: [
      async (request, _options, response) => {
        if (response.status !== 401 || isAuthEndpoint(request.url)) return response;

        const tokens = await runRefresh();
        if (!tokens) {
          unauthorizedHandler?.();
          return response;
        }
        const retried = new Request(request, {
          headers: new Headers(request.headers),
        });
        retried.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
        return ky(retried, { retry: 0 });
      },
    ],
    beforeError: [
      async (error) => {
        if (error instanceof HTTPError) {
          const apiError = await parseProblemDetail(error.response);
          return apiError as unknown as HTTPError;
        }
        return error;
      },
    ],
  },
});

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export { ApiError };
